import {
  ClearAll,
  DeleteSweep,
  MarkEmailRead,
  Notifications as NotificationsIcon,
  NotificationsNone as NotificationsNoneIcon,
} from '@mui/icons-material';
import {
  Avatar,
  Badge,
  Box,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Popover,
  Tooltip,
  Typography,
} from '@mui/material';
import { formatDistanceToNow } from 'date-fns';
import { tr } from 'date-fns/locale/tr';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotification } from '../contexts/NotificationContext';
import { colors } from '../theme/colors';

interface NotificationBellProps {
  size?: 'default' | 'small';
}

const NotificationBell: React.FC<NotificationBellProps> = ({
  size = 'default',
}) => {
  const {
    notifications,
    unreadCount,
    markAsRead,
    markAllAsRead,
    clearNotification,
    clearAll,
  } = useNotification();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const navigate = useNavigate();

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationClick = (notification: {
    id: string;
    link?: string;
    conversationId?: string;
    isRead: boolean;
  }) => {
    if (!notification.isRead) {
      markAsRead(notification.id);
    }
    if (notification.link) {
      // Eğer conversationId varsa, URL'e ekle
      if (notification.conversationId) {
        navigate(
          `${notification.link}?conversation=${notification.conversationId}`
        );
      } else {
        navigate(notification.link);
      }
    }
    handleClose();
  };

  const handleMarkAllAsRead = () => {
    markAllAsRead();
  };

  const handleClearAll = () => {
    // Önce tümünü okundu işaretle, sonra temizle
    markAllAsRead();
    clearAll();
  };

  const handleClearNotification = (
    e: React.MouseEvent,
    notificationId: string
  ) => {
    e.stopPropagation();
    clearNotification(notificationId);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'notification-popover' : undefined;

  // Bildirimleri tarihe göre grupla
  const groupedNotifications = notifications.reduce((acc, notif) => {
    const date = new Date(notif.timestamp);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    let groupKey = 'older';
    if (date.toDateString() === today.toDateString()) {
      groupKey = 'today';
    } else if (date.toDateString() === yesterday.toDateString()) {
      groupKey = 'yesterday';
    }

    if (!acc[groupKey]) {
      acc[groupKey] = [];
    }
    acc[groupKey].push(notif);
    return acc;
  }, {} as Record<string, typeof notifications>);

  const groupLabels: Record<string, string> = {
    today: 'Bugün',
    yesterday: 'Dün',
    older: 'Daha Önce',
  };

  return (
    <>
      <Tooltip title="Bildirimler">
        <IconButton
          onClick={handleClick}
          sx={{
            color: 'white',
            bgcolor: 'rgba(255, 255, 255, 0.1)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            width: size === 'small' ? 32 : 44,
            height: size === 'small' ? 32 : 44,
            '&:hover': {
              bgcolor: 'rgba(255, 255, 255, 0.2)',
              transform: 'scale(1.05)',
            },
            transition: 'all 0.2s ease',
          }}
        >
          <Badge
            badgeContent={unreadCount}
            sx={{
              '& .MuiBadge-badge': {
                fontSize: '0.7rem',
                minWidth: '18px',
                height: '18px',
                padding: '0 4px',
                background: colors.status.error,
                color: 'white',
                fontWeight: 700,
              },
            }}
          >
            {unreadCount > 0 ? (
              <NotificationsIcon
                sx={{ color: 'white', fontSize: size === 'small' ? 20 : 24 }}
              />
            ) : (
              <NotificationsNoneIcon
                sx={{ color: 'white', fontSize: size === 'small' ? 20 : 24 }}
              />
            )}
          </Badge>
        </IconButton>
      </Tooltip>

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        disableScrollLock={true}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            width: 380,
            maxWidth: '90vw',
            maxHeight: '80vh',
            mt: 1,
            borderRadius: 2,
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
            overflow: 'hidden',
          },
        }}
      >
        <Box
          sx={{
            background: colors.gradients.primary,
            color: 'white',
            p: 1.5,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: `1px solid ${colors.alpha.white10}`,
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              letterSpacing: '-0.5px',
              fontSize: '1.1rem',
            }}
          >
            Bildirimler
            {unreadCount > 0 && (
              <Typography
                component="span"
                sx={{
                  ml: 1,
                  fontSize: '0.875rem',
                  opacity: 0.9,
                  fontWeight: 500,
                }}
              >
                ({unreadCount} yeni)
              </Typography>
            )}
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {unreadCount > 0 && (
              <Tooltip title="Tümünü Okundu İşaretle">
                <IconButton
                  size="small"
                  onClick={handleMarkAllAsRead}
                  sx={{
                    color: 'white',
                    width: 32,
                    height: 32,
                    bgcolor: colors.alpha.white10,
                    border: `1px solid ${colors.alpha.white30}`,
                    '&:hover': {
                      bgcolor: colors.alpha.white10,
                      transform: 'scale(1.05)',
                    },
                    transition: 'all 0.2s ease',
                  }}
                >
                  <MarkEmailRead fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {notifications.length > 0 && (
              <Tooltip title="Tümünü Temizle">
                <IconButton
                  size="small"
                  onClick={handleClearAll}
                  sx={{
                    color: 'white',
                    width: 32,
                    height: 32,
                    bgcolor: colors.alpha.white10,
                    border: `1px solid ${colors.alpha.white30}`,
                    '&:hover': {
                      bgcolor: colors.alpha.white10,
                      transform: 'scale(1.05)',
                    },
                    transition: 'all 0.2s ease',
                  }}
                >
                  <DeleteSweep fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>

        <Box
          sx={{
            maxHeight: '60vh',
            overflowY: 'auto',
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-track': {
              background: '#f1f1f1',
            },
            '&::-webkit-scrollbar-thumb': {
              background: '#888',
              borderRadius: '4px',
              '&:hover': {
                background: '#555',
              },
            },
          }}
        >
          {notifications.length === 0 ? (
            <Box
              sx={{
                p: 4,
                textAlign: 'center',
                color: colors.text.secondary,
              }}
            >
              <NotificationsNoneIcon
                sx={{
                  fontSize: 48,
                  mb: 2,
                  opacity: 0.3,
                  color: colors.text.disabled,
                }}
              />
              <Typography variant="body2" sx={{ color: colors.text.secondary }}>
                Henüz bildirim yok
              </Typography>
            </Box>
          ) : (
            Object.keys(groupedNotifications).map((groupKey) => (
              <Box key={groupKey}>
                <Box
                  sx={{
                    px: 2,
                    py: 1,
                    bgcolor: colors.background.light,
                    borderBottom: `1px solid ${colors.border.light}`,
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 600,
                      color: colors.text.secondary,
                      textTransform: 'uppercase',
                      letterSpacing: 0.5,
                      fontSize: '0.7rem',
                    }}
                  >
                    {groupLabels[groupKey] || groupKey}
                  </Typography>
                </Box>
                <List sx={{ p: 0 }}>
                  {groupedNotifications[groupKey].map((notification) => (
                    <ListItem
                      key={notification.id}
                      disablePadding
                      sx={{
                        borderBottom: `1px solid ${colors.border.light}`,
                        bgcolor: notification.isRead
                          ? 'transparent'
                          : colors.alpha.primary05,
                        transition: 'background-color 0.2s',
                      }}
                    >
                      <ListItemButton
                        onClick={() => handleNotificationClick(notification)}
                        sx={{
                          py: 1.5,
                          px: 2,
                          '&:hover': {
                            bgcolor: colors.alpha.primary10,
                          },
                        }}
                      >
                        <Avatar
                          sx={{
                            width: 40,
                            height: 40,
                            background: colors.gradients.primary,
                            mr: 1.5,
                            fontWeight: 700,
                            fontSize: '1rem',
                            boxShadow: colors.shadows.card,
                          }}
                        >
                          {notification.senderName
                            ? notification.senderName.charAt(0).toUpperCase()
                            : '?'}
                        </Avatar>
                        <ListItemText
                          primary={
                            <Box
                              sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'flex-start',
                              }}
                            >
                              <Typography
                                variant="subtitle2"
                                sx={{
                                  fontWeight: notification.isRead ? 500 : 700,
                                  flex: 1,
                                  color: colors.text.primary,
                                  fontSize: '0.875rem',
                                }}
                              >
                                {notification.title}
                              </Typography>
                              {!notification.isRead && (
                                <Box
                                  sx={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: '50%',
                                    background: colors.gradients.primary,
                                    ml: 1,
                                    mt: 0.5,
                                    boxShadow: `0 0 4px ${colors.primary.main}40`,
                                  }}
                                />
                              )}
                            </Box>
                          }
                          secondary={
                            <>
                              <Typography
                                variant="body2"
                                sx={{
                                  color: colors.text.secondary,
                                  mt: 0.5,
                                  display: '-webkit-box',
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: 'vertical',
                                  overflow: 'hidden',
                                  fontSize: '0.8125rem',
                                  lineHeight: 1.4,
                                }}
                              >
                                {notification.message}
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: colors.text.disabled,
                                  mt: 0.5,
                                  display: 'block',
                                  fontSize: '0.7rem',
                                }}
                              >
                                {formatDistanceToNow(notification.timestamp, {
                                  addSuffix: true,
                                  locale: tr,
                                })}
                              </Typography>
                            </>
                          }
                        />
                        <IconButton
                          size="small"
                          onClick={(e) =>
                            handleClearNotification(e, notification.id)
                          }
                          sx={{
                            ml: 1,
                            opacity: 0.4,
                            width: 24,
                            height: 24,
                            color: colors.text.secondary,
                            '&:hover': {
                              opacity: 1,
                              color: colors.status.error,
                              bgcolor: colors.alpha.primary05,
                            },
                            transition: 'all 0.2s ease',
                          }}
                        >
                          <ClearAll fontSize="small" />
                        </IconButton>
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </Box>
            ))
          )}
        </Box>
      </Popover>
    </>
  );
};

export default NotificationBell;
